import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { useI18n } from '../i18n/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BookOpen, Ticket, Users, ScrollText, Plus, Pencil, Trash2, Inbox, Check, X, QrCode, Download, Star, GraduationCap, MailOpen, Settings, Upload, Eye, MessagesSquare } from 'lucide-react';

const emptyChapter = {
  number: '', title: '', subtitle: '', act: '', closing_word: '', author_note: '',
  song: '{\n  "title": "",\n  "style": "",\n  "lyrics": []\n}',
  keys: '{\n  "coeur": { "intro": [], "emotions": [], "learn": [] },\n  "sagesse": { "lessons": [] },\n  "phrase": { "text": "" },\n  "mentor": { "name": "", "letter": [] },\n  "dialogue": { "lines": [] },\n  "mission": { "lines": [] },\n  "carnet": { "note": [], "prompt": "Et toi ? Aujourd\'hui, j\'ai compris que..." },\n  "giveback": { "lines": [] },\n  "aventure": { "lines": [] },\n  "projection": { "lines": [] }\n}',
  is_published: true,
};

const chapterToForm = (ch) => ({
  number: ch.number, title: ch.title, subtitle: ch.subtitle || '', act: ch.act || '',
  closing_word: ch.closing_word || '', author_note: ch.author_note || '',
  song: JSON.stringify(ch.song || {}, null, 2),
  keys: JSON.stringify(ch.keys || {}, null, 2),
  is_published: ch.is_published,
});

const inputCls = 'h-12 rounded-2xl bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume focus:ring-4 focus:ring-or-400/40 font-bold';
const textareaCls = 'rounded-2xl bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume focus:ring-4 focus:ring-or-400/40';
const tableCls = 'w-full text-sm';
const thCls = 'p-4 font-bold text-brume text-left';
const rowCls = 'border-b border-nuit-700';

export default function AdminHome() {
  const [chapters, setChapters] = useState([]);
  const [codes, setCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [deleteRequestTarget, setDeleteRequestTarget] = useState(null);
  const [qrcodes, setQrcodes] = useState([]);
  const [ambassadors, setAmbassadors] = useState([]);
  const [masterclasses, setMasterclasses] = useState([]);
  const [editMc, setEditMc] = useState(null);
  const [mcForm, setMcForm] = useState({ title: '', description: '', date: '', location: '', capacity: '20', active: true });
  const [settings, setSettings] = useState({ landing_video_url: '' });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [regsFor, setRegsFor] = useState(null);
  const [regs, setRegs] = useState([]);
  const [deleteUserTarget, setDeleteUserTarget] = useState(null);
  const [deleteChapterTarget, setDeleteChapterTarget] = useState(null);
  const [profileReview, setProfileReview] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [editChapter, setEditChapter] = useState(null);
  const [form, setForm] = useState(emptyChapter);
  const [genRole, setGenRole] = useState('jeune');
  const [genCount, setGenCount] = useState('5');
  const [topics, setTopics] = useState([]);
  const [topicForm, setTopicForm] = useState({ chapter_number: '', title: '', prompt: '', challenge: '' });
  const [topicDialog, setTopicDialog] = useState(false);
  const [deleteTopicTarget, setDeleteTopicTarget] = useState(null);
  const { t, dateLocale } = useI18n();

  const roleLabel = (r) => t(`roles.${r}`, undefined, r);
  const statusLabel = (s) => (s === 'pending' ? t('admin.statusPending') : s === 'approved' ? t('admin.statusApproved') : t('admin.statusRejected'));

  const loadAll = () => {
    api.get('/admin/chapters').then((r) => setChapters(r.data.chapters)).catch(() => {});
    api.get('/admin/invitations').then((r) => setCodes(r.data.codes)).catch(() => {});
    api.get('/admin/users').then((r) => setUsers(r.data.users)).catch(() => {});
    api.get('/admin/audit').then((r) => setLogs(r.data.logs)).catch(() => {});
    api.get('/admin/access-requests').then((r) => setRequests(r.data.requests)).catch(() => {});
    api.get('/admin/qrcodes').then((r) => setQrcodes(r.data.qrcodes)).catch(() => {});
    api.get('/admin/ambassadors').then((r) => setAmbassadors(r.data.applications)).catch(() => {});
    api.get('/admin/masterclasses').then((r) => setMasterclasses(r.data.masterclasses)).catch(() => {});
    api.get('/admin/settings').then((r) => { setSettings(r.data.settings); setSettingsLoaded(true); })
      .catch(() => setSettingsLoaded(true));
    api.get('/admin/analytics').then((r) => setAnalytics(r.data)).catch(() => {});
    api.get('/admin/exchange/topics').then((r) => setTopics(r.data.topics)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const saveSettings = async () => {
    try {
      await api.put('/admin/settings', settings);
      toast.success(t('admin.settingsSaved'));
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  const audioUpload = async (slot, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.mp3')) { toast.error(t('admin.audioInvalid')); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error(t('admin.audioTooBig')); return; }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post(`/admin/settings/upload-audio?slot=${slot}`, fd);
      setSettings((s) => ({ ...s, [`landing_audio${slot}_url`]: r.data.path }));
      toast.success(t('admin.audioUploaded'));
    } catch (err) { toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : t('common.error')); }
  };

  const saveChapter = async () => {
    try {
      const payload = {
        number: parseInt(form.number, 10), title: form.title, subtitle: form.subtitle, act: form.act,
        closing_word: form.closing_word, author_note: form.author_note,
        song: JSON.parse(form.song || '{}'), keys: JSON.parse(form.keys || '{}'),
        is_published: form.is_published,
      };
      if (editChapter === 'new') await api.post('/admin/chapters', payload);
      else await api.put(`/admin/chapters/${editChapter}`, payload);
      toast.success(t('admin.chapterSaved'));
      setEditChapter(null);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('admin.chapterError'));
    }
  };

  const deleteChapter = async () => {
    if (deleteChapterTarget === null) return;
    try {
      await api.delete(`/admin/chapters/${deleteChapterTarget}`);
      toast.success(t('admin.chapterDeleted'));
      loadAll();
    } catch { toast.error(t('common.error')); }
    setDeleteChapterTarget(null);
  };

  const saveTopic = async () => {
    try {
      const payload = {
        chapter_number: topicForm.chapter_number ? parseInt(topicForm.chapter_number, 10) : null,
        title: topicForm.title, prompt: topicForm.prompt, challenge: topicForm.challenge,
      };
      await api.post('/admin/exchange/topics', payload);
      toast.success(t('admin.topicSaved'));
      setTopicDialog(false);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  const removeTopic = async () => {
    if (!deleteTopicTarget) return;
    try {
      await api.delete(`/admin/exchange/topics/${deleteTopicTarget}`);
      toast.success(t('admin.topicDeleted'));
      loadAll();
    } catch { toast.error(t('common.error')); }
    setDeleteTopicTarget(null);
  };

  const deleteUser = async () => {
    if (!deleteUserTarget) return;
    try {
      await api.delete(`/admin/users/${deleteUserTarget.user_id}`);
      toast.success(t('admin.userDeleted'));
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
    setDeleteUserTarget(null);
  };

  const resendConsent = async (u) => {
    try {
      await api.post(`/admin/users/${u.user_id}/resend-consent`);
      toast.success(t('admin.consentResent', { name: u.name }));
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  const generateCodes = async () => {
    try {
      const { data } = await api.post('/admin/invitations', { role: genRole, count: parseInt(genCount, 10) });
      toast.success(t('admin.codesGenerated', { n: data.codes.length }));
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  const toggleCode = async (code, active) => {
    try {
      await api.patch(`/admin/invitations/${code}`, { active });
      loadAll();
    } catch { toast.error(t('common.error')); }
  };

  const handleRequest = async (rid, action) => {
    try {
      const { data } = await api.post(`/admin/access-requests/${rid}/${action}`);
      toast.success(action === 'approve' ? t('admin.codeSent', { code: data.code }) : t('admin.requestRejected'));
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  const openProfileReview = async (a) => {
    setProfileReview(a);
    setProfilePhoto(null);
    if (a.photo_path) {
      try {
        const r = await api.get(`/ambassador-photo/${a.application_id}`, { responseType: 'blob' });
        setProfilePhoto(URL.createObjectURL(r.data));
      } catch { /* photo indisponible */ }
    }
  };

  const handleProfileReview = async (aid, action) => {
    try {
      await api.post(`/admin/ambassadors/${aid}/${action}`);
      toast.success(action === 'approve-profile' ? t('admin.profileApproved') : t('admin.profileRejected'));
      setProfileReview(null);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  const deleteRequest = async (rid) => {
    try {
      await api.delete(`/admin/access-requests/${rid}`);
      toast.success(t('admin.requestDeleted'));
      setDeleteRequestTarget(null);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  const handleAmbassador = async (aid, action) => {
    try {
      await api.post(`/admin/ambassadors/${aid}/${action}`);
      toast.success(action === 'approve' ? t('admin.ambassadorApproved') : t('admin.ambassadorRejected'));
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  const openMc = (mc) => {
    if (mc) {
      setMcForm({ title: mc.title, description: mc.description || '',
        date: mc.date ? mc.date.slice(0, 16) : '', location: mc.location || '',
        capacity: String(mc.capacity ?? 20), active: mc.active });
      setEditMc(mc.masterclass_id);
    } else {
      setMcForm({ title: '', description: '', date: '', location: '', capacity: '20', active: true });
      setEditMc('new');
    }
  };

  const saveMc = async () => {
    try {
      const payload = { title: mcForm.title, description: mcForm.description,
        date: new Date(mcForm.date).toISOString(), location: mcForm.location,
        capacity: parseInt(mcForm.capacity, 10) || 20, active: mcForm.active };
      if (editMc === 'new') await api.post('/admin/masterclasses', payload);
      else await api.put(`/admin/masterclasses/${editMc}`, payload);
      toast.success(t('admin.mcSaved'));
      setEditMc(null);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('admin.mcError')); }
  };

  const deleteMc = async (mid) => {
    try {
      await api.delete(`/admin/masterclasses/${mid}`);
      toast.success(t('admin.mcDeleted'));
      loadAll();
    } catch { toast.error(t('common.error')); }
  };

  const openRegs = async (mc) => {
    setRegsFor(mc);
    try {
      const { data } = await api.get(`/admin/masterclasses/${mc.masterclass_id}/registrations`);
      setRegs(data.registrations);
    } catch { setRegs([]); }
  };

  return (
    <div data-testid="admin-dashboard">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-creme">{t('nav.admin')}</h1>
        <p className="text-base sm:text-lg text-brume mt-3">{t('admin.subtitle')}</p>
      </motion.div>

      <Tabs defaultValue="chapters" className="mt-10">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 rounded-3xl bg-nuit-800 border border-nuit-700 p-2 h-auto">
          <TabsTrigger value="chapters" data-testid="tab-chapters" className="flex flex-col items-center justify-center gap-1.5 min-h-[64px] rounded-2xl px-3 py-3 font-bold text-sm text-brume transition-colors hover:bg-nuit-700 data-[state=active]:bg-or-400 data-[state=active]:text-nuit-950 max-sm:last:col-span-2"><BookOpen className="w-5 h-5" /> {t('admin.tabChapters')}</TabsTrigger>
          <TabsTrigger value="codes" data-testid="tab-codes" className="flex flex-col items-center justify-center gap-1.5 min-h-[64px] rounded-2xl px-3 py-3 font-bold text-sm text-brume transition-colors hover:bg-nuit-700 data-[state=active]:bg-or-400 data-[state=active]:text-nuit-950 max-sm:last:col-span-2"><Ticket className="w-5 h-5" /> {t('admin.tabCodes')}</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests" className="flex flex-col items-center justify-center gap-1.5 min-h-[64px] rounded-2xl px-3 py-3 font-bold text-sm text-brume transition-colors hover:bg-nuit-700 data-[state=active]:bg-or-400 data-[state=active]:text-nuit-950 max-sm:last:col-span-2"><Inbox className="w-5 h-5" /> {t('admin.tabRequests')}</TabsTrigger>
          <TabsTrigger value="ambassadors" data-testid="tab-ambassadors" className="flex flex-col items-center justify-center gap-1.5 min-h-[64px] rounded-2xl px-3 py-3 font-bold text-sm text-brume transition-colors hover:bg-nuit-700 data-[state=active]:bg-or-400 data-[state=active]:text-nuit-950 max-sm:last:col-span-2"><Star className="w-5 h-5" /> {t('admin.tabAmbassadors')}</TabsTrigger>
          <TabsTrigger value="masterclass" data-testid="tab-masterclass" className="flex flex-col items-center justify-center gap-1.5 min-h-[64px] rounded-2xl px-3 py-3 font-bold text-sm text-brume transition-colors hover:bg-nuit-700 data-[state=active]:bg-or-400 data-[state=active]:text-nuit-950 max-sm:last:col-span-2"><GraduationCap className="w-5 h-5" /> {t('admin.tabMasterclass')}</TabsTrigger>
          <TabsTrigger value="qrcodes" data-testid="tab-qrcodes" className="flex flex-col items-center justify-center gap-1.5 min-h-[64px] rounded-2xl px-3 py-3 font-bold text-sm text-brume transition-colors hover:bg-nuit-700 data-[state=active]:bg-or-400 data-[state=active]:text-nuit-950 max-sm:last:col-span-2"><QrCode className="w-5 h-5" /> {t('admin.tabQrcodes')}</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users" className="flex flex-col items-center justify-center gap-1.5 min-h-[64px] rounded-2xl px-3 py-3 font-bold text-sm text-brume transition-colors hover:bg-nuit-700 data-[state=active]:bg-or-400 data-[state=active]:text-nuit-950 max-sm:last:col-span-2"><Users className="w-5 h-5" /> {t('admin.tabUsers')}</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit" className="flex flex-col items-center justify-center gap-1.5 min-h-[64px] rounded-2xl px-3 py-3 font-bold text-sm text-brume transition-colors hover:bg-nuit-700 data-[state=active]:bg-or-400 data-[state=active]:text-nuit-950 max-sm:last:col-span-2"><ScrollText className="w-5 h-5" /> {t('admin.tabAudit')}</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings" className="flex flex-col items-center justify-center gap-1.5 min-h-[64px] rounded-2xl px-3 py-3 font-bold text-sm text-brume transition-colors hover:bg-nuit-700 data-[state=active]:bg-or-400 data-[state=active]:text-nuit-950 max-sm:last:col-span-2"><Settings className="w-5 h-5" /> {t('admin.tabSettings')}</TabsTrigger>
          <TabsTrigger value="topics" data-testid="tab-topics" className="flex flex-col items-center justify-center gap-1.5 min-h-[64px] rounded-2xl px-3 py-3 font-bold text-sm text-brume transition-colors hover:bg-nuit-700 data-[state=active]:bg-or-400 data-[state=active]:text-nuit-950 max-sm:last:col-span-2"><MessagesSquare className="w-5 h-5" /> {t('admin.tabTopics')}</TabsTrigger>
        </TabsList>

        <TabsContent value="chapters" className="mt-8">
          <div className="flex justify-between items-center mb-6">
            <p className="font-bold text-brume" data-testid="chapters-count">{t('admin.chaptersCount', { n: chapters.length })}</p>
            <Button data-testid="chapter-new-button" onClick={() => { setForm(emptyChapter); setEditChapter('new'); }}
              className="rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold gap-2">
              <Plus className="w-4 h-4" strokeWidth={2.5} /> {t('admin.newChapter')}
            </Button>
          </div>
          <div className="bg-nuit-800 border border-nuit-700 rounded-3xl overflow-x-auto">
            <table className={tableCls} data-testid="chapters-table">
              <thead>
                <tr className="border-b border-nuit-700">
                  <th className={thCls}>#</th><th className={thCls}>{t('admin.colTitle')}</th>
                  <th className={thCls}>{t('admin.colAct')}</th><th className={thCls}>{t('admin.colPublished')}</th><th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {chapters.map((ch) => (
                  <tr key={ch.number} className={`${rowCls} hover:bg-nuit-700/40`} data-testid={`chapter-row-${ch.number}`}>
                    <td className="p-4 font-fredoka font-semibold text-lg text-or-400">{ch.number}</td>
                    <td className="p-4 font-bold text-creme">{ch.title}</td>
                    <td className="p-4"><span className="bg-nuit-900 text-brume px-3 py-1 rounded-full text-xs font-bold">{(ch.act || '').split('—')[0].trim()}</span></td>
                    <td className="p-4 text-creme">{ch.is_published ? '✓' : '—'}</td>
                    <td className="p-4 text-right whitespace-nowrap">
                      <button data-testid={`chapter-edit-${ch.number}`} onClick={() => { setForm(chapterToForm(ch)); setEditChapter(ch.number); }}
                        className="w-9 h-9 rounded-full hover:bg-nuit-600 inline-flex items-center justify-center text-brume"><Pencil className="w-4 h-4" /></button>
                      <button data-testid={`chapter-delete-${ch.number}`} onClick={() => setDeleteChapterTarget(ch.number)}
                        className="w-9 h-9 rounded-full hover:bg-rose-950 inline-flex items-center justify-center text-brume hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="codes" className="mt-8">
          <div className="bg-nuit-800 border border-nuit-700 rounded-3xl p-6 flex flex-wrap items-end gap-4 mb-8">
            <div>
              <label className="block text-sm font-bold text-creme mb-2">{t('admin.role')}</label>
              <Select value={genRole} onValueChange={setGenRole}>
                <SelectTrigger data-testid="code-role-select" className="w-48 h-12 rounded-2xl bg-nuit-900 border-nuit-600 text-creme font-bold">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-nuit-800 border-nuit-600 rounded-2xl">
                  {['jeune', 'parent', 'educateur', 'admin'].map((v) => <SelectItem key={v} value={v} data-testid={`code-role-${v}`} className="text-creme">{roleLabel(v)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-bold text-creme mb-2">{t('admin.count')}</label>
              <Input data-testid="code-count-input" type="number" min="1" max="100" value={genCount}
                onChange={(e) => setGenCount(e.target.value)} className={`w-28 ${inputCls}`} />
            </div>
            <Button onClick={generateCodes} data-testid="code-generate-button"
              className="h-12 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold px-6">{t('admin.generate')}</Button>
          </div>
          <div className="bg-nuit-800 border border-nuit-700 rounded-3xl overflow-x-auto">
            <table className={tableCls} data-testid="codes-table">
              <thead>
                <tr className="border-b border-nuit-700">
                  <th className={thCls}>{t('admin.colCode')}</th><th className={thCls}>{t('admin.role')}</th>
                  <th className={thCls}>{t('admin.colUses')}</th><th className={thCls}>{t('admin.colActive')}</th>
                </tr>
              </thead>
              <tbody>
                {codes.map((c) => (
                  <tr key={c.code} className={rowCls} data-testid={`code-row-${c.code}`}>
                    <td className="p-4 font-mono font-bold text-creme">{c.code}</td>
                    <td className="p-4"><span className="bg-nuit-900 text-brume px-3 py-1 rounded-full text-xs font-bold">{roleLabel(c.role)}</span></td>
                    <td className="p-4 font-semibold text-brume">{c.uses}/{c.max_uses}</td>
                    <td className="p-4">
                      <Switch checked={c.active} onCheckedChange={(v) => toggleCode(c.code, v)} data-testid={`code-toggle-${c.code}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="requests" className="mt-8">
          <div className="bg-nuit-800 border border-nuit-700 rounded-3xl overflow-x-auto">
            <table className={tableCls} data-testid="requests-table">
              <thead>
                <tr className="border-b border-nuit-700">
                  <th className={thCls}>{t('admin.colName')}</th><th className={thCls}>{t('admin.colEmail')}</th><th className={thCls}>{t('admin.role')}</th>
                  <th className={thCls}>{t('admin.colMessage')}</th><th className={thCls}>{t('admin.colStatus')}</th><th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.request_id} className={rowCls} data-testid={`request-row-${r.request_id}`}>
                    <td className="p-4 font-bold text-creme">{r.name}</td>
                    <td className="p-4 text-brume">{r.email}</td>
                    <td className="p-4"><span className="bg-nuit-900 text-brume px-3 py-1 rounded-full text-xs font-bold">{roleLabel(r.role)}</span></td>
                    <td className="p-4 text-brume text-xs max-w-[240px]">{r.message || '—'}</td>
                    <td className="p-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold
                        ${r.status === 'pending' ? 'bg-amber-400/20 text-amber-300' : r.status === 'approved' ? 'bg-green-400/20 text-green-300' : 'bg-rose-400/20 text-rose-300'}`}>
                        {statusLabel(r.status)}
                      </span>
                      {r.auto && (
                        <span className="ml-2 px-3 py-1 rounded-full text-xs font-bold bg-or-400/20 text-or-300"
                          data-testid={`request-auto-${r.request_id}`}>{t('admin.autoBadge')}</span>
                      )}
                    </td>
                    <td className="p-4 text-right whitespace-nowrap">
                      {r.status === 'pending' && (
                        <>
                          <button data-testid={`request-approve-${r.request_id}`} onClick={() => handleRequest(r.request_id, 'approve')}
                            title={t('admin.approveTitle')}
                            className="w-9 h-9 rounded-full hover:bg-green-900/50 inline-flex items-center justify-center text-green-400"><Check className="w-4 h-4" /></button>
                          <button data-testid={`request-reject-${r.request_id}`} onClick={() => handleRequest(r.request_id, 'reject')}
                            title={t('admin.rejectTitle')}
                            className="w-9 h-9 rounded-full hover:bg-rose-950 inline-flex items-center justify-center text-rose-400"><X className="w-4 h-4" /></button>
                        </>
                      )}
                      {r.status === 'approved' && r.code && <span className="font-mono text-xs text-or-300">{r.code}</span>}
                      <button data-testid={`request-delete-${r.request_id}`} onClick={() => setDeleteRequestTarget(r)}
                        title={t('admin.requestDeleteTitle')}
                        className="w-9 h-9 rounded-full hover:bg-rose-950 inline-flex items-center justify-center text-rose-400"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
                {requests.length === 0 && (
                  <tr><td colSpan="6" className="p-8 text-center text-brume font-semibold" data-testid="requests-empty">{t('admin.requestsEmpty')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="ambassadors" className="mt-8">
          <div className="bg-nuit-800 border border-nuit-700 rounded-3xl overflow-x-auto">
            <table className={tableCls} data-testid="ambassadors-table">
              <thead>
                <tr className="border-b border-nuit-700">
                  <th className={thCls}>{t('admin.colName')}</th><th className={thCls}>{t('admin.colEmail')}</th><th className={thCls}>{t('admin.colOrganisation')}</th>
                  <th className={thCls}>{t('admin.colMessage')}</th><th className={thCls}>{t('admin.colStatus')}</th><th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {ambassadors.map((a) => (
                  <tr key={a.application_id} className={rowCls} data-testid={`ambassador-row-${a.application_id}`}>
                    <td className="p-4 font-bold text-creme">{a.name}</td>
                    <td className="p-4 text-brume">{a.email}</td>
                    <td className="p-4 text-brume">{a.organisation || '—'}</td>
                    <td className="p-4 text-brume text-xs max-w-[240px]">{a.message || '—'}</td>
                    <td className="p-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold
                        ${a.status === 'pending' ? 'bg-amber-400/20 text-amber-300' : a.status === 'approved' ? 'bg-green-400/20 text-green-300' : 'bg-rose-400/20 text-rose-300'}`}>
                        {statusLabel(a.status)}
                      </span>
                      {a.profile_approved && (
                        <span className="ml-2 px-3 py-1 rounded-full text-xs font-bold bg-or-400/20 text-or-300"
                          data-testid={`ambassador-live-${a.application_id}`}>{t('admin.onLanding')}</span>
                      )}
                    </td>
                    <td className="p-4 text-right whitespace-nowrap">
                      {a.status === 'approved' && (a.photo_path || (a.social_links || []).length > 0) && (
                        <button data-testid={`ambassador-profile-${a.application_id}`} onClick={() => openProfileReview(a)}
                          title={t('admin.ambassadorProfileTitle')}
                          className="w-9 h-9 rounded-full hover:bg-nuit-700 inline-flex items-center justify-center text-or-400"><Eye className="w-4 h-4" /></button>
                      )}
                      {a.status === 'pending' && (
                        <>
                          <button data-testid={`ambassador-approve-${a.application_id}`} onClick={() => handleAmbassador(a.application_id, 'approve')}
                            title={t('admin.ambassadorApproveTitle')}
                            className="w-9 h-9 rounded-full hover:bg-green-900/50 inline-flex items-center justify-center text-green-400"><Check className="w-4 h-4" /></button>
                          <button data-testid={`ambassador-reject-${a.application_id}`} onClick={() => handleAmbassador(a.application_id, 'reject')}
                            title={t('admin.rejectTitle')}
                            className="w-9 h-9 rounded-full hover:bg-rose-950 inline-flex items-center justify-center text-rose-400"><X className="w-4 h-4" /></button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {ambassadors.length === 0 && (
                  <tr><td colSpan="6" className="p-8 text-center text-brume font-semibold" data-testid="ambassadors-empty">{t('admin.ambassadorsEmpty')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="masterclass" className="mt-8">
          <div className="flex justify-between items-center mb-6">
            <p className="font-bold text-brume" data-testid="masterclass-count">{t('admin.mcCount', { n: masterclasses.length })}</p>
            <Button data-testid="masterclass-new-button" onClick={() => openMc(null)}
              className="rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold gap-2">
              <Plus className="w-4 h-4" strokeWidth={2.5} /> {t('admin.newMc')}
            </Button>
          </div>
          <div className="bg-nuit-800 border border-nuit-700 rounded-3xl overflow-x-auto">
            <table className={tableCls} data-testid="masterclass-table">
              <thead>
                <tr className="border-b border-nuit-700">
                  <th className={thCls}>{t('admin.colTitle')}</th><th className={thCls}>{t('admin.colDate')}</th><th className={thCls}>{t('admin.colLocation')}</th>
                  <th className={thCls}>{t('admin.colRegistrations')}</th><th className={thCls}>{t('admin.colActiveF')}</th><th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {masterclasses.map((mc) => (
                  <tr key={mc.masterclass_id} className={rowCls} data-testid={`masterclass-row-${mc.masterclass_id}`}>
                    <td className="p-4 font-bold text-creme">{mc.title}</td>
                    <td className="p-4 text-brume whitespace-nowrap">{new Date(mc.date).toLocaleString(dateLocale)}</td>
                    <td className="p-4 text-brume">{mc.location || t('admin.online')}</td>
                    <td className="p-4">
                      <button onClick={() => openRegs(mc)} data-testid={`masterclass-regs-${mc.masterclass_id}`}
                        className="bg-nuit-900 text-or-300 px-3 py-1 rounded-full text-xs font-bold hover:bg-nuit-600">
                        {t('admin.seeRegs', { count: mc.registered_count, capacity: mc.capacity })}
                      </button>
                    </td>
                    <td className="p-4 text-creme">{mc.active ? '✓' : '—'}</td>
                    <td className="p-4 text-right whitespace-nowrap">
                      <button data-testid={`masterclass-edit-${mc.masterclass_id}`} onClick={() => openMc(mc)}
                        className="w-9 h-9 rounded-full hover:bg-nuit-600 inline-flex items-center justify-center text-brume"><Pencil className="w-4 h-4" /></button>
                      <button data-testid={`masterclass-delete-${mc.masterclass_id}`} onClick={() => deleteMc(mc.masterclass_id)}
                        className="w-9 h-9 rounded-full hover:bg-rose-950 inline-flex items-center justify-center text-brume hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
                {masterclasses.length === 0 && (
                  <tr><td colSpan="6" className="p-8 text-center text-brume font-semibold" data-testid="masterclass-empty">{t('admin.mcEmpty')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="qrcodes" className="mt-8">
          <p className="text-sm text-brume mb-6">{t('admin.qrInfo')}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4" data-testid="qrcodes-grid">
            {qrcodes.map((q) => (
              <div key={q.number} className="bg-nuit-800 border border-nuit-700 rounded-2xl p-3 text-center"
                data-testid={`qrcode-${q.number}`}>
                <img src={q.data_url} alt={`QR ${q.number}`} className="w-full rounded-lg bg-white p-1" />
                <p className="text-xs font-bold text-creme mt-2 truncate" title={q.title}>{t('admin.qrChapter', { n: q.number })}</p>
                <a href={q.data_url} download={`qrcode-chapitre-${q.number}.png`} data-testid={`qrcode-download-${q.number}`}
                  className="inline-flex items-center gap-1 text-xs font-bold text-or-400 hover:text-or-300 mt-1">
                  <Download className="w-3 h-3" /> PNG
                </a>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-8">
          <div className="bg-nuit-800 border border-nuit-700 rounded-3xl overflow-x-auto">
            <table className={tableCls} data-testid="users-table">
              <thead>
                <tr className="border-b border-nuit-700">
                  <th className={thCls}>{t('admin.colName')}</th><th className={thCls}>{t('admin.colEmail')}</th>
                  <th className={thCls}>{t('admin.role')}</th><th className={thCls}>{t('admin.colStatus')}</th><th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className={rowCls} data-testid={`user-row-${u.user_id}`}>
                    <td className="p-4 font-bold text-creme">{u.name}</td>
                    <td className="p-4 text-brume">{u.email}</td>
                    <td className="p-4"><span className="bg-nuit-900 text-brume px-3 py-1 rounded-full text-xs font-bold">{roleLabel(u.role)}</span></td>
                    <td className="p-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${u.status === 'active' ? 'bg-green-400/20 text-green-300' : 'bg-amber-400/20 text-amber-300'}`}>
                        {u.status}
                      </span>
                    </td>
                    <td className="p-4 text-right whitespace-nowrap">
                      {u.status === 'pending_consent' && (
                        <button data-testid={`user-resend-${u.user_id}`} onClick={() => resendConsent(u)} title={t('admin.resendTitle')}
                          className="w-9 h-9 rounded-full hover:bg-nuit-600 inline-flex items-center justify-center text-or-400"><MailOpen className="w-4 h-4" /></button>
                      )}
                      {u.role !== 'admin' && (
                        <button data-testid={`user-delete-${u.user_id}`} onClick={() => setDeleteUserTarget(u)} title={t('admin.userDeleteTitle')}
                          className="w-9 h-9 rounded-full hover:bg-rose-950 inline-flex items-center justify-center text-brume hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="audit" className="mt-8">
          <div className="bg-nuit-800 border border-nuit-700 rounded-3xl overflow-x-auto">
            <table className={tableCls} data-testid="audit-table">
              <thead>
                <tr className="border-b border-nuit-700">
                  <th className={thCls}>{t('admin.colDate')}</th><th className={thCls}>{t('admin.colActor')}</th>
                  <th className={thCls}>{t('admin.colAction')}</th><th className={thCls}>{t('admin.colDetails')}</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.log_id} className={rowCls} data-testid={`audit-row-${l.log_id}`}>
                    <td className="p-4 text-brume whitespace-nowrap">{new Date(l.timestamp).toLocaleString(dateLocale)}</td>
                    <td className="p-4 font-semibold text-creme">{l.actor_email}</td>
                    <td className="p-4"><span className="bg-nuit-900 text-or-300 px-3 py-1 rounded-full text-xs font-bold">{l.action}</span></td>
                    <td className="p-4 text-brume font-mono text-xs">{JSON.stringify(l.details)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="settings" className="mt-8">
          <div className="max-w-2xl bg-nuit-800 border border-nuit-700 rounded-3xl p-8" data-testid="settings-panel">
            <h2 className="text-xl font-semibold text-creme mb-6">{t('admin.setTitle')}</h2>
            <h3 className="text-lg font-bold text-creme font-fredoka mb-3">{t('admin.analyticsTitle')}</h3>
            {analytics ? (
              <div data-testid="admin-analytics" className="mb-10">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                  {[
                    ['users_active', 'admin.an.users'],
                    ['users_pending_consent', 'admin.an.pendingConsent'],
                    ['signups_7d', 'admin.an.signups7'],
                    ['signups_30d', 'admin.an.signups30'],
                    ['chapters_completed', 'admin.an.chapters'],
                    ['keys_answered', 'admin.an.keys'],
                    ['diary_entries', 'admin.an.diary'],
                    ['family_links', 'admin.an.family'],
                    ['consents_pending', 'admin.an.consents'],
                    ['access_requests_pending', 'admin.an.requests'],
                    ['crm_contacts', 'admin.an.crm'],
                    ['masterclass_registrations', 'admin.an.mc'],
                    ['contact_messages', 'admin.an.messages'],
                  ].map(([field, labelKey]) => (
                    <div key={field} data-testid={`analytics-card-${field}`}
                      className="bg-nuit-900 border border-nuit-600 rounded-2xl px-4 py-3">
                      <p className="text-2xl font-bold text-or-400 font-fredoka">{analytics[field]}</p>
                      <p className="text-xs text-brume font-bold">{t(labelKey)}</p>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 mb-4" data-testid="analytics-roles">
                  {Object.entries(analytics.users_by_role || {}).map(([role, count]) => (
                    <span key={role} data-testid={`analytics-role-${role}`}
                      className="text-xs font-bold bg-nuit-900 border border-nuit-600 rounded-full px-3 py-1 text-brume">
                      {t(`roles.${role}`, undefined, role)} : <span className="text-or-300">{count}</span>
                    </span>
                  ))}
                </div>
                <p className="text-xs font-bold text-brume uppercase tracking-widest mb-2">{t('admin.an.recent')}</p>
                {analytics.recent_signups?.length ? (
                  <ul className="space-y-1 mb-2" data-testid="analytics-recent">
                    {analytics.recent_signups.map((u, i) => (
                      <li key={i} className="text-sm text-brume flex justify-between gap-3">
                        <span className="text-creme font-bold truncate">{u.name}</span>
                        <span className="shrink-0">{t(`roles.${u.role}`, undefined, u.role)} · {(u.created_at || '').slice(0, 10)}</span>
                      </li>
                    ))}
                  </ul>
                ) : <p className="text-sm text-brume italic">{t('admin.an.empty')}</p>}
              </div>
            ) : <p className="text-sm text-brume italic mb-10" data-testid="analytics-loading">…</p>}
            <label className="block text-sm font-bold text-creme mb-2">{t('admin.setVideo')}</label>
            <Input data-testid="settings-video-input" value={settings.landing_video_url || ''}
              onChange={(e) => setSettings({ ...settings, landing_video_url: e.target.value })}
              placeholder="https://www.youtube.com/watch?v=…" className={inputCls} />
            <p className="text-xs text-brume mt-2 mb-6">{t('admin.setVideoHelp')}</p>
            <label className="block text-sm font-bold text-creme mb-2">{t('admin.setNetworkVideo')}</label>
            <Input data-testid="settings-network-video-input" value={settings.network_video_url || ''}
              onChange={(e) => setSettings({ ...settings, network_video_url: e.target.value })}
              placeholder="https://www.youtube.com/watch?v=…" disabled={!settingsLoaded} className={`${inputCls} mb-6`} />
            <Label className="text-creme font-bold">{t('admin.setAudio1')}</Label>
            <Input data-testid="settings-audio1-title" value={settings.landing_audio1_title || ''}
              onChange={(e) => setSettings({ ...settings, landing_audio1_title: e.target.value })}
              placeholder={t('admin.audioTitlePh')} disabled={!settingsLoaded} className={`${inputCls} mb-2`} />
            <div className="flex gap-2">
              <Input data-testid="settings-audio1-input" value={settings.landing_audio1_url || ''}
                onChange={(e) => setSettings({ ...settings, landing_audio1_url: e.target.value })}
                placeholder="/assets/musique-1.mp3 ou https://…" disabled={!settingsLoaded} className={`${inputCls} flex-1`} />
              <Button type="button" variant="outline" disabled={!settingsLoaded} data-testid="settings-audio1-upload"
                onClick={() => document.getElementById('audio-file-1').click()}
                className="h-12 rounded-2xl border-or-400/60 text-or-300 font-bold hover:bg-or-400/10 gap-2 shrink-0">
                <Upload className="w-4 h-4" strokeWidth={2.5} /> {t('admin.audioUpload')}
              </Button>
              <input id="audio-file-1" type="file" accept="audio/mpeg,.mp3" className="hidden" data-testid="settings-audio1-file"
                onChange={(e) => { audioUpload(1, e.target.files?.[0]); e.target.value = ''; }} />
            </div>
            <Label className="text-creme font-bold mt-4">{t('admin.setAudio2')}</Label>
            <Input data-testid="settings-audio2-title" value={settings.landing_audio2_title || ''}
              onChange={(e) => setSettings({ ...settings, landing_audio2_title: e.target.value })}
              placeholder={t('admin.audioTitlePh')} disabled={!settingsLoaded} className={`${inputCls} mb-2`} />
            <div className="flex gap-2">
              <Input data-testid="settings-audio2-input" value={settings.landing_audio2_url || ''}
                onChange={(e) => setSettings({ ...settings, landing_audio2_url: e.target.value })}
                placeholder="/assets/musique-2.mp3 ou https://…" disabled={!settingsLoaded} className={`${inputCls} flex-1`} />
              <Button type="button" variant="outline" disabled={!settingsLoaded} data-testid="settings-audio2-upload"
                onClick={() => document.getElementById('audio-file-2').click()}
                className="h-12 rounded-2xl border-or-400/60 text-or-300 font-bold hover:bg-or-400/10 gap-2 shrink-0">
                <Upload className="w-4 h-4" strokeWidth={2.5} /> {t('admin.audioUpload')}
              </Button>
              <input id="audio-file-2" type="file" accept="audio/mpeg,.mp3" className="hidden" data-testid="settings-audio2-file"
                onChange={(e) => { audioUpload(2, e.target.files?.[0]); e.target.value = ''; }} />
            </div>
            <p className="text-xs text-brume mt-2 mb-6">{t('admin.setAudioHelp')}</p>
            <h3 className="text-lg font-bold text-creme font-fredoka mt-8 mb-1">{t('admin.mcEventsTitle')}</h3>
            <p className="text-xs text-brume mb-3">{t('admin.mcEventsHelp')}</p>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="bg-nuit-900 border border-nuit-600 rounded-2xl p-4 space-y-2" data-testid={`settings-mc-ev-${n}`}>
                  <p className="text-xs font-bold text-or-300 uppercase tracking-widest">{t('admin.mcEvTitle', { n })}</p>
                  <Input data-testid={`settings-mc-ev${n}-date`} value={settings[`mc_ev${n}_date`] || ''} placeholder={t(`landing.mc.ev${n}.date`)} disabled={!settingsLoaded}
                    onChange={(e) => setSettings({ ...settings, [`mc_ev${n}_date`]: e.target.value })} className={inputCls} />
                  <Input data-testid={`settings-mc-ev${n}-audience`} value={settings[`mc_ev${n}_audience`] || ''} placeholder={t(`landing.mc.ev${n}.audience`)} disabled={!settingsLoaded}
                    onChange={(e) => setSettings({ ...settings, [`mc_ev${n}_audience`]: e.target.value })} className={inputCls} />
                  <Input data-testid={`settings-mc-ev${n}-objective`} value={settings[`mc_ev${n}_objective`] || ''} placeholder={t(`landing.mc.ev${n}.objective`)} disabled={!settingsLoaded}
                    onChange={(e) => setSettings({ ...settings, [`mc_ev${n}_objective`]: e.target.value })} className={inputCls} />
                  <Input data-testid={`settings-mc-ev${n}-subject`} value={settings[`mc_ev${n}_subject`] || ''} placeholder={t(`landing.mc.ev${n}.subject`)} disabled={!settingsLoaded}
                    onChange={(e) => setSettings({ ...settings, [`mc_ev${n}_subject`]: e.target.value })} className={inputCls} />
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between bg-nuit-900 border-2 border-or-400/50 rounded-2xl px-4 py-3 mb-2" data-testid="settings-maintenance-row">
              <span className="text-sm font-bold text-creme">{t('admin.maintenanceMode')}</span>
              <Switch checked={settings.maintenance_mode === '1'} disabled={!settingsLoaded}
                onCheckedChange={(v) => setSettings({ ...settings, maintenance_mode: v ? '1' : '0' })}
                data-testid="settings-toggle-maintenance" />
            </div>
            <p className="text-xs text-brume mb-8">{t('admin.maintenanceHelp')}</p>
            <div className="flex items-center justify-between gap-4 bg-nuit-900 border border-nuit-600 rounded-2xl px-4 py-3 mb-8" data-testid="settings-access-jeune-row">
              <div>
                <span className="text-sm font-bold text-creme">{t('admin.accessJeuneAuto')}</span>
                <p className="text-xs text-brume mt-1 max-w-lg">{t('admin.accessJeuneAutoHelp')}</p>
              </div>
              <Switch checked={settings.access_jeune_auto !== '0'} disabled={!settingsLoaded}
                onCheckedChange={(v) => setSettings({ ...settings, access_jeune_auto: v ? '1' : '0' })}
                data-testid="settings-toggle-access-jeune-auto" />
            </div>
            <h3 className="text-lg font-bold text-creme font-fredoka mb-3">{t('admin.setPages')}</h3>
            <div className="space-y-3 mb-8">
              {['page_livre', 'page_medias', 'page_masterclass', 'page_apropos', 'page_faq', 'page_presse', 'page_contact'].map((p) => (
                <div key={p} className="flex items-center justify-between bg-nuit-900 border border-nuit-600 rounded-2xl px-4 py-3">
                  <span className="text-sm font-bold text-creme">{t(`admin.pageToggle.${p}`)}</span>
                  <Switch checked={settings[p] !== '0'} disabled={!settingsLoaded}
                    onCheckedChange={(v) => setSettings({ ...settings, [p]: v ? '1' : '0' })}
                    data-testid={`settings-toggle-${p}`} />
                </div>
              ))}
            </div>
            <h3 className="text-lg font-bold text-creme font-fredoka mb-3">{t('admin.setContent')}</h3>
            <div className="space-y-4 mb-8">
              {['content_landing_quote', 'content_landing_closing', 'content_contact_reply48'].map((k) => (
                <div key={k}>
                  <label className="block text-sm font-bold text-creme mb-2">{t(`admin.contentField.${k}`)}</label>
                  <Input data-testid={`settings-content-${k}`} value={settings[k] || ''}
                    onChange={(e) => setSettings({ ...settings, [k]: e.target.value })}
                    placeholder={t('admin.contentEmpty')} className={inputCls} />
                </div>
              ))}
            </div>
            <Button onClick={saveSettings} disabled={!settingsLoaded} data-testid="settings-save"
              className="rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold px-8">{t('educator.save')}</Button>
          </div>
        </TabsContent>

        <TabsContent value="topics" className="mt-8">
          <div className="flex justify-between items-center mb-6">
            <p className="font-bold text-brume" data-testid="topics-count">{t('admin.topicsCount', { n: topics.length })}</p>
            <Button data-testid="topic-new-button" onClick={() => { setTopicForm({ chapter_number: '', title: '', prompt: '', challenge: '' }); setTopicDialog(true); }}
              className="rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold gap-2">
              <Plus className="w-4 h-4" strokeWidth={2.5} /> {t('admin.topicNew')}
            </Button>
          </div>
          <div className="bg-nuit-800 border border-nuit-700 rounded-3xl overflow-x-auto">
            <table className={tableCls} data-testid="topics-table">
              <thead>
                <tr className="border-b border-nuit-700">
                  <th className={thCls}>{t('admin.colChapter')}</th><th className={thCls}>{t('admin.topicTitle')}</th>
                  <th className={thCls}>{t('admin.colPrompt')}</th><th className={thCls}>{t('admin.colChallenge')}</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {topics.map((tp) => (
                  <tr key={tp.topic_id} className={`${rowCls} hover:bg-nuit-700/40`} data-testid={`topic-row-${tp.topic_id}`}>
                    <td className="p-4">
                      <span className="bg-nuit-900 text-brume px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap">
                        {tp.chapter_number ? t('exchange.chapterN', { n: tp.chapter_number }) : t('admin.topicGeneral')}
                      </span>
                    </td>
                    <td className="p-4 font-bold text-creme">{tp.title}</td>
                    <td className="p-4 text-brume max-w-xs"><span className="line-clamp-2">{tp.prompt}</span></td>
                    <td className="p-4 text-brume max-w-xs"><span className="line-clamp-2">{tp.challenge}</span></td>
                    <td className="p-4 text-right whitespace-nowrap">
                      <button data-testid={`topic-delete-${tp.topic_id}`} onClick={() => setDeleteTopicTarget(tp.topic_id)}
                        className="w-9 h-9 rounded-full hover:bg-rose-950 inline-flex items-center justify-center text-brume hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={topicDialog} onOpenChange={setTopicDialog}>
        <DialogContent className="max-w-lg rounded-3xl bg-nuit-800 border-nuit-700" data-testid="topic-dialog">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-2xl text-creme">{t('admin.topicNew')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="block text-sm font-bold text-creme mb-2">{t('admin.topicChapter')}</label>
              <Select value={topicForm.chapter_number || 'general'}
                onValueChange={(v) => setTopicForm({ ...topicForm, chapter_number: v === 'general' ? '' : v })}>
                <SelectTrigger data-testid="topic-chapter-select" className="h-12 rounded-2xl bg-nuit-900 border-nuit-600 text-creme font-bold">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-nuit-800 border-nuit-600 rounded-2xl max-h-64">
                  <SelectItem value="general" data-testid="topic-chapter-general" className="text-creme">{t('admin.topicGeneral')}</SelectItem>
                  {chapters.map((ch) => (
                    <SelectItem key={ch.number} value={String(ch.number)} data-testid={`topic-chapter-${ch.number}`} className="text-creme">
                      {ch.number} — {ch.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-bold text-creme mb-2">{t('admin.topicTitle')}</label>
              <Input data-testid="topic-title-input" value={topicForm.title}
                onChange={(e) => setTopicForm({ ...topicForm, title: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-bold text-creme mb-2">{t('admin.topicPrompt')}</label>
              <Textarea data-testid="topic-prompt-input" rows={3} value={topicForm.prompt}
                onChange={(e) => setTopicForm({ ...topicForm, prompt: e.target.value })} className={textareaCls} />
            </div>
            <div>
              <label className="block text-sm font-bold text-creme mb-2">{t('admin.topicChallenge')}</label>
              <Textarea data-testid="topic-challenge-input" rows={3} value={topicForm.challenge}
                onChange={(e) => setTopicForm({ ...topicForm, challenge: e.target.value })} className={textareaCls} />
            </div>
            <Button data-testid="topic-save-button" onClick={saveTopic}
              disabled={!topicForm.title.trim() || !topicForm.prompt.trim() || !topicForm.challenge.trim()}
              className="w-full h-12 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold">{t('educator.save')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTopicTarget} onOpenChange={(open) => !open && setDeleteTopicTarget(null)}>
        <AlertDialogContent className="rounded-3xl bg-nuit-800 border-nuit-700" data-testid="topic-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-fredoka text-2xl text-creme">{t('admin.topicDeleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="text-brume">{t('admin.topicDeleteText')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="topic-delete-cancel"
              className="rounded-full bg-transparent border-2 border-nuit-600 text-brume font-bold hover:bg-nuit-700 hover:text-creme">
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={removeTopic} data-testid="topic-delete-confirm"
              className="rounded-full bg-rose-500 hover:bg-rose-400 text-white font-bold">
              {t('educator.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editChapter} onOpenChange={(open) => !open && setEditChapter(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-nuit-800 border-nuit-700" data-testid="chapter-dialog">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-2xl text-creme">
              {editChapter === 'new' ? t('admin.newChapter') : t('admin.chapterEdit', { n: editChapter })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-creme mb-2">{t('admin.chapterNumber')}</label>
                <Input data-testid="chapter-number-input" type="number" value={form.number} disabled={editChapter !== 'new'}
                  onChange={(e) => setForm({ ...form, number: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-bold text-creme mb-2">{t('admin.colAct')}</label>
                <Input data-testid="chapter-act-input" value={form.act} placeholder={t('admin.chapterActPlaceholder')}
                  onChange={(e) => setForm({ ...form, act: e.target.value })} className={inputCls} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-bold text-creme mb-2">{t('admin.colTitle')}</label>
              <Input data-testid="chapter-title-input" value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-bold text-creme mb-2">{t('admin.chapterSubtitle')}</label>
              <Input data-testid="chapter-subtitle-input" value={form.subtitle}
                onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-bold text-creme mb-2">{t('admin.chapterClosing')}</label>
              <Input data-testid="chapter-closing-input" value={form.closing_word}
                onChange={(e) => setForm({ ...form, closing_word: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-bold text-creme mb-2">{t('admin.chapterAuthor')}</label>
              <Textarea data-testid="chapter-author-input" value={form.author_note} rows={2}
                onChange={(e) => setForm({ ...form, author_note: e.target.value })} className={textareaCls} />
            </div>
            <div>
              <label className="block text-sm font-bold text-creme mb-2">{t('admin.chapterSong')}</label>
              <Textarea data-testid="chapter-song-input" value={form.song} rows={5}
                onChange={(e) => setForm({ ...form, song: e.target.value })} className={`${textareaCls} font-mono text-xs`} />
            </div>
            <div>
              <label className="block text-sm font-bold text-creme mb-2">{t('admin.chapterKeys')}</label>
              <Textarea data-testid="chapter-keys-input" value={form.keys} rows={12}
                onChange={(e) => setForm({ ...form, keys: e.target.value })} className={`${textareaCls} font-mono text-xs`} />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} data-testid="chapter-published-switch" />
              <span className="text-sm font-bold text-creme">{t('admin.colPublished')}</span>
            </div>
            <Button onClick={saveChapter} data-testid="chapter-save-button"
              className="w-full h-12 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold">{t('educator.save')}</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!editMc} onOpenChange={(open) => !open && setEditMc(null)}>
        <DialogContent className="max-w-lg rounded-3xl bg-nuit-800 border-nuit-700" data-testid="masterclass-dialog">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-2xl text-creme">
              {editMc === 'new' ? t('admin.newMc') : t('admin.mcEdit')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="block text-sm font-bold text-creme mb-2">{t('admin.mcTitle')}</label>
              <Input data-testid="mc-title-input" value={mcForm.title} placeholder={t('admin.mcTitlePlaceholder')}
                onChange={(e) => setMcForm({ ...mcForm, title: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-bold text-creme mb-2">{t('admin.mcDescription')}</label>
              <Textarea data-testid="mc-description-input" rows={3} value={mcForm.description}
                onChange={(e) => setMcForm({ ...mcForm, description: e.target.value })} className={textareaCls} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-creme mb-2">{t('admin.mcDate')}</label>
                <Input data-testid="mc-date-input" type="datetime-local" value={mcForm.date}
                  onChange={(e) => setMcForm({ ...mcForm, date: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-bold text-creme mb-2">{t('admin.mcCapacity')}</label>
                <Input data-testid="mc-capacity-input" type="number" min="1" value={mcForm.capacity}
                  onChange={(e) => setMcForm({ ...mcForm, capacity: e.target.value })} className={inputCls} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-bold text-creme mb-2">{t('admin.mcLocation')}</label>
              <Input data-testid="mc-location-input" value={mcForm.location} placeholder={t('admin.mcLocationPlaceholder')}
                onChange={(e) => setMcForm({ ...mcForm, location: e.target.value })} className={inputCls} />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={mcForm.active} onCheckedChange={(v) => setMcForm({ ...mcForm, active: v })} data-testid="mc-active-switch" />
              <span className="text-sm font-bold text-creme">{t('admin.mcActive')}</span>
            </div>
            <Button onClick={saveMc} data-testid="mc-save-button" disabled={!mcForm.title.trim() || !mcForm.date}
              className="w-full h-12 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold">{t('educator.save')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!regsFor} onOpenChange={(open) => !open && setRegsFor(null)}>
        <DialogContent className="max-w-lg rounded-3xl bg-nuit-800 border-nuit-700" data-testid="regs-dialog">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-2xl text-creme">{t('admin.regsTitle', { title: regsFor?.title })}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2 max-h-[50vh] overflow-y-auto" data-testid="regs-list">
            {regs.map((r) => (
              <div key={r.registration_id} className="flex items-center justify-between bg-nuit-900 rounded-2xl px-4 py-3"
                data-testid={`reg-${r.registration_id}`}>
                <div>
                  <p className="font-bold text-creme text-sm">{r.name}</p>
                  <p className="text-xs text-brume">{r.email}</p>
                </div>
                <span className="text-xs font-bold text-brume">{roleLabel(r.role)}</span>
              </div>
            ))}
            {regs.length === 0 && <p className="text-brume font-semibold text-center py-6">{t('admin.regsEmpty')}</p>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!profileReview} onOpenChange={(open) => !open && setProfileReview(null)}>
        <DialogContent className="max-w-lg rounded-3xl bg-nuit-800 border-nuit-700" data-testid="ambassador-profile-dialog">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-2xl text-creme">{t('admin.profileReviewTitle')} — {profileReview?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {profilePhoto ? (
              <img src={profilePhoto} alt={profileReview?.name || ''} data-testid="ambassador-review-photo"
                className="w-28 h-28 rounded-full object-cover mx-auto border-2 border-or-400/50" />
            ) : (
              <p className="text-brume text-sm text-center" data-testid="ambassador-review-nophoto">{t('admin.noProfileYet')}</p>
            )}
            {(profileReview?.social_links || []).length > 0 && (
              <div className="space-y-2" data-testid="ambassador-review-links">
                {profileReview.social_links.map((l, i) => (
                  <a key={i} href={l.url} target="_blank" rel="noopener noreferrer"
                    data-testid={`ambassador-review-link-${i}`}
                    className="block bg-nuit-900 rounded-2xl px-4 py-3 text-sm font-bold text-or-300 hover:text-or-400 truncate">
                    {t(`ambProfile.networks.${l.network}`, undefined, l.network)} — {l.url}
                  </a>
                ))}
              </div>
            )}
            {!profileReview?.profile_approved ? (
              <Button onClick={() => handleProfileReview(profileReview?.application_id, 'approve-profile')}
                data-testid="ambassador-approve-profile-button"
                className="w-full h-12 rounded-full bg-green-500 hover:bg-green-400 text-white font-bold">
                {t('admin.approveProfile')}
              </Button>
            ) : (
              <Button onClick={() => handleProfileReview(profileReview?.application_id, 'reject-profile')}
                data-testid="ambassador-reject-profile-button"
                className="w-full h-12 rounded-full bg-rose-500 hover:bg-rose-400 text-white font-bold">
                {t('admin.rejectProfile')}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteRequestTarget !== null} onOpenChange={(open) => !open && setDeleteRequestTarget(null)}>
        <AlertDialogContent className="rounded-3xl bg-nuit-800 border-nuit-700" data-testid="request-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-fredoka text-2xl text-creme">{t('admin.requestDeleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="text-brume">
              {t('admin.requestDeleteConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="request-delete-cancel"
              className="rounded-full bg-transparent border-2 border-nuit-600 text-brume font-bold hover:bg-nuit-700 hover:text-creme">
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteRequest(deleteRequestTarget?.request_id)} data-testid="request-delete-confirm"
              className="rounded-full bg-rose-500 hover:bg-rose-400 text-white font-bold">
              {t('admin.requestDeleteTitle')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteChapterTarget !== null} onOpenChange={(open) => !open && setDeleteChapterTarget(null)}>
        <AlertDialogContent className="rounded-3xl bg-nuit-800 border-nuit-700" data-testid="chapter-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-fredoka text-2xl text-creme">{t('admin.chapterDeleteConfirm')}</AlertDialogTitle>
            <AlertDialogDescription className="text-brume">
              {t('admin.chapterDeleteText', { n: deleteChapterTarget })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="chapter-delete-cancel"
              className="rounded-full bg-transparent border-2 border-nuit-600 text-brume font-bold hover:bg-nuit-700 hover:text-creme">
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={deleteChapter} data-testid="chapter-delete-confirm"
              className="rounded-full bg-rose-500 hover:bg-rose-400 text-white font-bold">
              {t('educator.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteUserTarget} onOpenChange={(open) => !open && setDeleteUserTarget(null)}>
        <AlertDialogContent className="rounded-3xl bg-nuit-800 border-nuit-700" data-testid="user-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-fredoka text-2xl text-creme">{t('admin.userDeleteDialogTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="text-brume">
              {t('admin.userDeleteDialogText', { name: deleteUserTarget?.name, email: deleteUserTarget?.email })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-delete-cancel"
              className="rounded-full bg-transparent border-2 border-nuit-600 text-brume font-bold hover:bg-nuit-700 hover:text-creme">
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={deleteUser} data-testid="user-delete-confirm"
              className="rounded-full bg-rose-500 hover:bg-rose-400 text-white font-bold">
              {t('admin.deleteForever')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
